import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "./supabase";
// O farejador de tipo por magic bytes mora em `supabase/functions/_shared/`, e é
// importado daqui de propósito.
//
// Ele é a MESMA pergunta feita em dois lugares — no navegador, antes de subir, e
// na Edge Function, antes de chamar o modelo —, e ele nasceu de medição (105
// arquivos sem extensão que eram PDF, 80 HEIC, 35 JFIF). Uma segunda cópia em
// `app/src/lib` divergiria da primeira, e a que divergisse em silêncio seria
// justamente a que ninguém lê: é a cicatriz do `calendario_data_da_obrigacao`
// (000106/000109), um motor completo escrito duas vezes sem consumidor.
//
// O módulo não tem import nenhum e usa só `Uint8Array`, `TextDecoder` e
// `String.fromCharCode` — o que existe nos dois ambientes.
import { extensaoDoMime, farejar } from "../../../supabase/functions/_shared/tipo-de-arquivo";

// O IO da Faculdade: o boletim, a prova e o balcão da Secretaria
// (000128-000135).
//
// Este módulo TRANSPORTA. Quem decide o que o aluno pode ver é o banco — as
// funções `security definer` com filtro por aluno escrito à mão, e a RLS de
// `sec_documentos`. Quem decide o que aparece na tela são as páginas. Aqui não
// há regra de acesso nem de apresentação.
//
// ---------------------------------------------------------------------------
// Por que quase tudo passa por RPC, e não por `from(...).select()`
// ---------------------------------------------------------------------------
// `pos_questoes` e `pos_alternativas` não têm grant para `authenticated`, e a
// razão não é privacidade: `pos_alternativas.correta` diz qual é a resposta
// certa. Um SELECT pela Data API entregaria o gabarito ao navegador e a prova
// deixaria de medir conhecimento.
//
// `pos_tentativas`, `pos_respostas` e o catálogo acadêmico também estão fechados,
// pelo mesmo motivo da vitrine ter virado view de dono na 000047: a leitura sai
// por função, com o filtro por aluno explícito, e assim não existe uma segunda
// porta para a mesma decisão.
//
// A exceção é `sec_documentos` / `sec_emitidos` / `sec_tipos`, que são lidas por
// RLS de dono (`user_id = meu_profile_id() or eh_suporte()`). Ali o dono é claro
// e a policy basta; as ESCRITAS continuam por RPC, porque `authenticated` não tem
// UPDATE — sem isso o aluno aprovaria o próprio diploma com um PATCH.
//
// ---------------------------------------------------------------------------
// A prova: o que NUNCA chega aqui
// ---------------------------------------------------------------------------
// `iniciarProva` devolve as questões com as alternativas SEM o campo `correta`.
// O gabarito só existe no retorno de `finalizarProva`, depois de a nota estar
// gravada — é o único lugar do sistema em que ele sai do banco. Não guarde esse
// retorno em nenhum estado que sobreviva à tela de resultado.

// ─────────────────────────────────────────────────────────────────────────────
// O boletim
// ─────────────────────────────────────────────────────────────────────────────

export type LinhaDoBoletim = {
  curso_slug: string;
  curso_nome: string | null;
  disciplina_id: string;
  disciplina: string;
  carga_horaria_horas: number | null;
  docente_nome: string | null;
  docente_titulacao: string | null;
  ordem: number | null;
  /**
   * A disciplina está na grade da turma do aluno.
   *
   * `false` significa "você tem nota aqui, mas esta disciplina não é da sua
   * turma" — 2.659 tentativas reais estão nesse estado (1,1%), de quem trocou de
   * turma ou de disciplina que saiu da grade. A tela mostra as duas metades em
   * seções separadas: esconder as de fora é perder nota de aluno.
   */
  na_grade: boolean;
  total_questoes: number;
  tentativas: number;
  tentativas_restantes: number;
  melhor_nota: number | null;
  ultima_nota: number | null;
  ultima_em: string | null;
  /** A tentativa aberta, se houver. É o que faz o botão dizer "Continuar". */
  em_andamento: string | null;
  /**
   * Pendente · aprovado · reprovado, pela MAIOR nota (decisão de Victor, 31/08).
   *
   * Quem decide é o BANCO (`pos_meu_boletim`, com `pos_nota_minima()`), e não a
   * tela — a mesma nota de corte decide se o certificado pode ser pedido
   * (`sec_pode_solicitar`, trava 4). Recalcular aqui criaria a segunda definição
   * de "aprovado", e um dia a tela diria verde onde a trava diz não.
   */
  situacao: "pendente" | "aprovado" | "reprovado";
};

/**
 * Uma tentativa do histórico — o que a aba "Histórico" lista.
 *
 * Existe separada do boletim porque as duas respondem perguntas diferentes: o
 * boletim é "como estou em cada avaliação" (uma linha por disciplina, a MAIOR
 * nota), e o histórico é "o que eu já fiz" (uma linha por tentativa, em ordem de
 * data). Foi o pedido da reunião de 31/08, com essas palavras.
 */
export type LinhaDoHistorico = {
  id: string;
  curso_slug: string;
  disciplina_id: string;
  disciplina: string;
  /** O número da tentativa daquela disciplina — 1 a 5, ou mais com liberação. */
  numero: number;
  nota: number | null;
  acertos: number | null;
  situacao: "em_andamento" | "finalizada" | "abandonada";
  /** Nulo quando a tentativa não terminou: ali "aprovada" não é dizível. */
  aprovada: boolean | null;
  iniciada_em: string;
  finalizada_em: string | null;
  na_grade: boolean;
};

/**
 * Por que a grade do aluno pode estar vazia.
 *
 * São QUATRO motivos, e a tela precisa dizer qual — o que o aluno faz em cada
 * caso é diferente (falar com a secretaria, esperar, ou nada). "A ausência de uma
 * linha não informa nada" é a cicatriz da aba Prazos da Admissão: tela vazia sem
 * motivo é lida como "está tudo bem".
 */
export type SituacaoDoCurso = {
  curso_slug: string;
  curso_nome: string | null;
  turma_numero: number | null;
  turma_texto: string | null;
  motivo:
    | "ok"
    | "sem_matricula"
    | "sem_turma"
    | "turma_desconhecida"
    | "grade_vazia"
    | `matricula_${string}`;
  disciplinas: number;
};

export async function lerBoletim(): Promise<LinhaDoBoletim[]> {
  const { data, error } = await supabase.rpc("pos_meu_boletim");
  if (error) throw error;
  return (data ?? []) as LinhaDoBoletim[];
}

export async function lerSituacao(): Promise<SituacaoDoCurso[]> {
  const { data, error } = await supabase.rpc("pos_minha_situacao");
  if (error) throw error;
  return (data ?? []) as SituacaoDoCurso[];
}

export async function lerHistorico(): Promise<LinhaDoHistorico[]> {
  const { data, error } = await supabase.rpc("pos_minhas_tentativas", {
    p_disciplina: null,
  });
  if (error) throw error;
  return (data ?? []) as LinhaDoHistorico[];
}

/** O texto de cada motivo, num lugar só. A tela não monta frase de estado. */
export function textoDaSituacao(s: SituacaoDoCurso): { titulo: string; corpo: string } | null {
  switch (s.motivo) {
    case "ok":
      return null;
    case "sem_matricula":
      return {
        titulo: "Matrícula não encontrada",
        corpo:
          "Você tem este curso, mas não encontramos a sua matrícula. Fale com a secretaria — " +
          "ela consegue registrar sem que você precise fazer nada.",
      };
    case "sem_turma":
      return {
        titulo: "Turma ainda não definida",
        corpo:
          "A sua matrícula está registrada e a turma ainda não. As disciplinas aparecem aqui " +
          "assim que a secretaria definir a sua turma.",
      };
    case "turma_desconhecida":
      return {
        titulo: "Turma a conferir",
        corpo:
          `A sua matrícula está na turma "${s.turma_texto ?? "?"}", que não encontramos na grade ` +
          "deste curso. A secretaria precisa conferir — e as suas notas continuam guardadas.",
      };
    case "grade_vazia":
      return {
        titulo: "Grade em montagem",
        corpo:
          "A sua turma está registrada e as disciplinas dela ainda não foram cadastradas. " +
          "Nada se perdeu: as notas que você já tem continuam aqui.",
      };
    default:
      // `matricula_cancelada`, `matricula_trancada` — o status vem do banco, e a
      // tela diz qual é em vez de inventar um texto por status desconhecido.
      return {
        titulo: `Matrícula ${s.motivo.replace("matricula_", "")}`,
        corpo:
          "A sua matrícula neste curso não está ativa. O seu histórico continua aqui; " +
          "para voltar a cursar, fale com a secretaria.",
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// A prova
// ─────────────────────────────────────────────────────────────────────────────

export type AlternativaDaProva = {
  letra: string;
  conteudo: string;
  // NÃO existe `correta` aqui, e a ausência é a peça: `pos_iniciar_tentativa`
  // monta o payload coluna por coluna justamente para que ela não apareça.
};

export type QuestaoDaProva = {
  id: string;
  /** Blocos tipados, no vocabulário de `TextoRico` — nunca HTML. */
  enunciado: unknown;
  enunciado_texto: string;
  /** A letra que o aluno já marcou, quando ele está retomando. */
  marcada: string | null;
  alternativas: AlternativaDaProva[];
};

export type Prova = {
  tentativa_id: string;
  disciplina_id: string;
  disciplina: string;
  numero: number;
  tentativas_restantes: number;
  questoes: QuestaoDaProva[];
};

export type ItemDoGabarito = {
  questao_id: string;
  enunciado_texto: string;
  marcada: string | null;
  correta: string;
  acertou: boolean;
};

export type Resultado = {
  nota: number;
  acertos: number;
  total: number;
  gabarito: ItemDoGabarito[];
};

/**
 * Abre a prova, ou RETOMA a que estava aberta.
 *
 * Retomar não é conveniência: sem isso, fechar a aba no meio queimaria uma das
 * cinco tentativas, e o aluno não teria como recuperar sozinho.
 */
export async function iniciarProva(disciplinaId: string): Promise<Prova> {
  const { data, error } = await supabase.rpc("pos_iniciar_tentativa", {
    p_disciplina: disciplinaId,
  });
  if (error) throw error;
  return data as Prova;
}

/** Grava a letra marcada. NÃO diz se acertou — a correção é no finalizar. */
export async function responder(
  tentativaId: string,
  questaoId: string,
  letra: string,
): Promise<void> {
  const { error } = await supabase.rpc("pos_responder", {
    p_tentativa: tentativaId,
    p_questao: questaoId,
    p_letra: letra,
  });
  if (error) throw error;
}

export async function finalizarProva(tentativaId: string): Promise<Resultado> {
  const { data, error } = await supabase.rpc("pos_finalizar_tentativa", {
    p_tentativa: tentativaId,
  });
  if (error) throw error;
  // A função devolve `returns table`, então o PostgREST manda um array de uma
  // linha. Pegar `[0]` sem conferir daria `undefined` num erro silencioso.
  const linha = Array.isArray(data) ? data[0] : data;
  if (!linha) throw new Error("a prova não devolveu resultado");
  return linha as Resultado;
}

// ─────────────────────────────────────────────────────────────────────────────
// A Secretaria
// ─────────────────────────────────────────────────────────────────────────────

export type TipoDeDocumento = {
  id: string;
  nome: string;
  fluxo: "envio" | "solicitacao";
  prazo_dias: number | null;
  exige_pagamento: boolean;
  ordem: number | null;
  /**
   * Este envio conta para a trava 2 do certificado. São QUATRO hoje, e a coluna
   * existe separada de `ativo` de propósito: um tipo pode ser oferecido sem ser
   * exigido, e sem a coluna a trava mudaria de sentido no dia em que a equipe
   * ligasse um quinto tipo de envio.
   */
  obrigatorio: boolean;
  /** Este pedido é o certificado — o único com as cinco travas. */
  exige_conclusao: boolean;
};

/**
 * O que o aluno pode pedir agora, e por que não.
 *
 * A decisão inteira vem do banco (`sec_travas_do_curso` → `sec_pode_solicitar`).
 * A tela desenha; ela não soma travas nem compara notas — a mesma nota de corte
 * decide o selo do boletim, e duas contas em JavaScript divergiriam.
 */
export type TravaDoPedido = {
  tipo_id: string;
  nome: string;
  prazo_dias: number | null;
  exige_pagamento: boolean;
  exige_conclusao: boolean;
  ordem: number | null;
  pode: boolean;
  /**
   * Por que não. São treze valores, e cada um pede uma ação diferente do aluno —
   * é por isso que a tela mostra o `detalhe` junto e não um "indisponível" seco.
   */
  motivo:
    | "ok"
    | "sem_sessao"
    | "tipo_desconhecido"
    | "sem_matricula"
    | "dados_incompletos"
    | "documentos_faltando"
    | "documentos_reprovados"
    | "documentos_em_analise"
    | "grade_nao_publicada"
    | "avaliacoes_pendentes"
    | "prazo_nao_configurado"
    | "sem_data_de_matricula"
    | "prazo_minimo";
  detalhe: string | null;
  /** Quantos faltam, quando o motivo tem quantidade. Nulo quando não tem. */
  faltam: number | null;
};

export async function lerTravas(cursoSlug: string): Promise<TravaDoPedido[]> {
  const { data, error } = await supabase.rpc("sec_travas_do_curso", {
    p_curso_slug: cursoSlug,
  });
  if (error) throw error;
  return (data ?? []) as TravaDoPedido[];
}

export type StatusDeEnvio = "em_analise" | "aprovado" | "reprovado";
export type StatusDeSolicitacao =
  | "aguardando_documento"
  | "aguardando_pagamento"
  /**
   * O certificado que o n8n já emitiu e a secretaria ainda não liberou (000149).
   *
   * Pedido de Victor em 01/09: *"o n8n emite mas ele não fica disponível de
   * imediato pro aluno, ele passa pela validação manual da secretaria"*. Neste
   * estado o `anexo_path` do PEDIDO é nulo de propósito — é isso que impede o
   * aluno de baixar antes da aprovação, e o PDF vive só em `sec_emitidos`.
   */
  /**
   * O aluno da Focus pagou e anexou o comprovante; a secretaria vai conferir
   * (000242).
   *
   * Victor descreveu este estado como "Em análise", e é isso que a tela DIZ —
   * mas o `em_analise` do banco é do fluxo de ENVIO, e os dois vocabulários são
   * separados por `sec_status_do_fluxo` desde sempre. Reusar a palavra teria
   * posto um comprovante de pagamento na fila de envios do balcão, cujo painel é
   * construído em volta de `sec_analises` — e a análise por IA o recusaria com
   * `nao_e_envio`, que é o certo e não ajuda quem está olhando a tela.
   */
  | "comprovante_em_analise"
  | "aguardando_conferencia"
  | "entregue";

export type MeuDocumento = {
  id: string;
  tipo_id: string | null;
  nome: string;
  fluxo: "envio" | "solicitacao";
  status: StatusDeEnvio | StatusDeSolicitacao;
  curso_slug: string | null;
  prazo_dias: number | null;
  exige_pagamento: boolean;
  anexo_path: string | null;
  anexo_externo_url: string | null;
  /**
   * A mensagem da equipe. 67.664 linhas têm — é onde a secretaria diz POR QUE
   * reprovou, e reprovação sem motivo devolve o aluno ao balcão sem saber o que
   * corrigir.
   */
  mensagem: string | null;
  criado_em: string;
  decidido_em: string | null;
  /** Quantas vezes este documento já foi enviado antes deste. */
  envios_anteriores: number;
  /**
   * A data que o diploma estampa, lida pela IA (000278). Nula em todo tipo que
   * não é diploma, e também no diploma que não traz data nenhuma.
   */
  colacao_data: string | null;
  /**
   * QUAL data do documento é a de cima — e ela não é enfeite: um diploma
   * estampa até três (colação de grau, conclusão do curso, expedição), e o
   * campo antigo do prompt pedia uma só sem dizer qual. Medido em 22/09, 35 das
   * 37.010 análises de diploma mencionavam colação.
   *
   * A tela usa isto para NOMEAR a data em vez de chamar tudo de colação: dizer
   * "colou grau em" numa data de expedição é afirmar o que o documento não
   * afirma, e quem lê decide com base nisso.
   */
  colacao_origem: "colacao" | "conclusao" | "expedicao" | null;
};

/** Como cada procedência de data se apresenta ao aluno (000278). */
export const ROTULO_DA_COLACAO: Record<
  NonNullable<MeuDocumento["colacao_origem"]>,
  { titulo: string; nota: string | null }
> = {
  colacao: { titulo: "Colou grau em", nota: null },
  // Os dois abaixo dizem o que são E o que não são. Sem a segunda metade, o
  // aluno (e a secretaria) leem qualquer data do diploma como colação — que é
  // justamente a confusão que a 000278 existe para desfazer.
  conclusao: { titulo: "Conclusão do curso em", nota: "o diploma não informa a data de colação" },
  expedicao: { titulo: "Diploma expedido em", nota: "o diploma não informa a data de colação" },
};

/**
 * Os documentos que o hub sabe emitir sozinho.
 *
 * Esta lista mora num lugar SÓ, e é por isso que ela existe como tipo: quando o
 * histórico parcial entrou (000237), ela estava escrita em quatro lugares deste
 * arquivo mais um ternário na tela — a mesma situação do `aguardando_conferencia`
 * da 000149, em que um estado novo passou despercebido por quatro funções que
 * enumeravam os antigos e todas continuaram respondendo com confiança a coisa
 * errada.
 */
export type TipoDeEmissao = "declaracao" | "certificado" | "historico";

/** Como cada um se chama na tela. */
export const NOME_DA_EMISSAO: Record<TipoDeEmissao, string> = {
  declaracao: "Declaração",
  certificado: "Certificado",
  historico: "Histórico",
};

/**
 * O que o banco mandou, quando ele é um tipo que esta versão da tela conhece.
 *
 * Devolve `null` para qualquer outra coisa — e quem chama DIZ que não
 * reconheceu, em vez de escolher um dos conhecidos. É a cicatriz do `default`
 * que afirma um fato: o ternário anterior (`=== "certificado" ? … : "declaracao"`)
 * teria rotulado todo histórico parcial como "Declaração", sem erro nenhum.
 */
export function tipoDeEmissao(valor: unknown): TipoDeEmissao | null {
  return valor === "declaracao" || valor === "certificado" || valor === "historico" ? valor : null;
}

export type DocumentoEmitido = {
  id: string;
  curso_slug: string | null;
  curso_origem: string | null;
  tipo: TipoDeEmissao;
  numero: string;
  codigo_verificacao: string;
  emitido_em: string | null;
  /**
   * O PDF que a EMISSÃO registrou (000287) — só nas do hub, que emite desde
   * 16/09. As 6.338 do Bubble chegam NULAS, e é o certo: o Bubble guardou o
   * número e o código, nunca o arquivo, e emprestar o PDF de um pedido seria
   * adivinhar qual documento é aquele número (só 2.092 casam 1 para 1).
   *
   * Quando vem, abre: o banco só o devolve se a emissão tem pedido entregue, que
   * é o que a policy `secretaria_le_emitido` exige para liberar o download.
   */
  anexo_path: string | null;
};

export async function lerTipos(): Promise<TipoDeDocumento[]> {
  const { data, error } = await supabase
    .from("sec_tipos")
    .select("id, nome, fluxo, prazo_dias, exige_pagamento, ordem, obrigatorio, exige_conclusao")
    .eq("ativo", true)
    .order("ordem", { ascending: true, nullsFirst: false })
    .order("nome");
  if (error) throw error;
  return (data ?? []) as TipoDeDocumento[];
}

/**
 * Os PEDIDOS do aluno — o atual de cada (tipo, curso).
 *
 * Desde a 000286 esta leitura NÃO traz os envios: o documento que o aluno manda
 * é dele, e não do curso, e a leitura dele é `lerMeusEnvios`. O pedido continua
 * sendo de um curso — uma declaração de matrícula da Pós DP não serve na Pós RT.
 */
export async function lerMeusDocumentos(): Promise<MeuDocumento[]> {
  const { data, error } = await supabase.rpc("sec_meus_documentos");
  if (error) throw error;
  return (data ?? []) as MeuDocumento[];
}

/**
 * Um documento que o aluno ENVIOU, com o papel dele na decisão (000286).
 *
 * O documento é do ALUNO, não do curso — decisão de Victor em 22/09: o RG
 * aprovado na Pós DP é o mesmo RG na Perícia. Por tipo, o banco devolve até
 * DUAS linhas:
 *
 *   · `vale` — o aprovado mais novo, de qualquer curso. É ele que as travas do
 *     certificado contam, e é ele que a tela mostra como o estado do documento;
 *   · `atual` — o envio mais novo. Quando é o mesmo que vale, é uma linha só;
 *     quando é outro (o aluno mandou de novo, e o novo está em análise ou foi
 *     reprovado), ele aparece como a tentativa, AO LADO do que vale.
 *
 * Um envio novo não desfaz o aprovado: com "o mais novo vale", 17 pares
 * (aluno, curso) que passavam nas travas perderiam o direito por causa de um
 * envio feito em outra pós. O aprovado só deixa de valer quando a secretaria
 * reprova AQUELE documento.
 *
 * A tela não recalcula nada disto: as travas leem a MESMA regra
 * (`sec_envios_do_aluno`), e duas contas divergiriam — um dia a tela diria
 * "Aprovado" onde a trava diz "falta".
 */
export type MeuEnvio = {
  id: string;
  tipo_id: string | null;
  nome: string;
  status: StatusDeEnvio;
  /** Onde foi ENVIADO — a procedência, não o dono. */
  curso_slug: string | null;
  /**
   * O nome do curso de origem, do catálogo. Vem do banco porque o documento
   * continua valendo depois que o acesso àquele curso expira, e nessa hora a
   * lista de cursos da tela não o tem.
   */
  curso_nome: string | null;
  anexo_path: string | null;
  anexo_externo_url: string | null;
  mensagem: string | null;
  criado_em: string;
  decidido_em: string | null;
  colacao_data: string | null;
  colacao_origem: MeuDocumento["colacao_origem"];
  atual: boolean;
  vale: boolean;
  /** Quantos envios deste tipo o aluno já fez, em TODOS os cursos. */
  envios: number;
};

export async function lerMeusEnvios(): Promise<MeuEnvio[]> {
  const { data, error } = await supabase.rpc("sec_meus_envios");
  if (error) throw error;
  return (data ?? []) as MeuEnvio[];
}

/**
 * Os documentos oficiais emitidos PARA O ALUNO.
 *
 * ANTES DE TROCAR ISTO POR UM `select`, LEIA (000136): a primeira versão fazia
 * `supabase.from("sec_emitidos").select(...)` sem filtro por aluno, contando com
 * a RLS. A policy é `user_id = meu_profile_id() OR eh_suporte()`, e o `or` está
 * certo — a secretaria precisa ver o que já foi emitido para atender um pedido
 * de segunda via.
 *
 * Só que a conta de um operador satisfaz `eh_suporte()`: a tela dele recebeu os
 * **6.338 emitidos de todos os alunos**, tendo zero próprios. A aba saiu com
 * 141.111 pixels de altura e a mesma linha repetida 87 vezes — e foi a CAPTURA
 * que mostrou, não uma asserção.
 *
 * A regra: **quando a policy tem `or eh_suporte()`, a leitura do aluno sai por
 * função com filtro explícito.** Para um aluno comum os dois caminhos dão o mesmo
 * resultado, e é isso que faz o defeito dormir — ele só aparece na conta que a
 * equipe usa para conferir.
 */
export async function lerEmitidos(): Promise<DocumentoEmitido[]> {
  const { data, error } = await supabase.rpc("sec_meus_emitidos");
  if (error) throw error;
  return (data ?? []) as DocumentoEmitido[];
}

/**
 * Qualquer coisa que tenha arquivo para baixar.
 *
 * Existe para o botão de anexo servir às TRÊS listas da tela (o pedido atual, o
 * entregue anterior e o contrato) sem que nenhuma delas precise fingir ser um
 * `MeuDocumento`. O botão só lê estes dois campos.
 */
export type ComArquivo = {
  anexo_path: string | null;
  anexo_externo_url: string | null;
};

/**
 * Um documento ENTREGUE que não é o atual do seu (tipo, curso) — 000260.
 *
 * `sec_meus_documentos` devolve UM por (tipo, curso), o mais novo. Então o
 * segundo pedido do mesmo documento esconde o primeiro, mesmo quando o primeiro
 * foi ENTREGUE e tem PDF. Isso valia para todo mundo e ficou visível quando os
 * 148 certificados digitais entregues foram para o tipo ativo: 142 deles têm um
 * pedido mais novo em aberto, e sem esta lista os alunos deixariam de alcançar
 * pela tela um documento que já têm.
 */
export type EntregueAnterior = {
  id: string;
  tipo_id: string | null;
  nome: string;
  curso_slug: string | null;
  anexo_path: string | null;
  anexo_externo_url: string | null;
  criado_em: string;
  decidido_em: string | null;
};

export async function lerEntreguesAnteriores(): Promise<EntregueAnterior[]> {
  const { data, error } = await supabase.rpc("sec_meus_entregues_anteriores");
  if (error) throw error;
  return (data ?? []) as EntregueAnterior[];
}

/**
 * O ACERVO do aluno — tudo que a casa entregou e continua sendo dele (000281).
 *
 * É a leitura da tela `/meus-documentos`, e a diferença para as duas acima é
 * que ela NÃO olha vigência de acesso: o requisito 3.5 é justamente que o
 * certificado não expire com o prazo do curso. Quem sai é só o acesso
 * CANCELADO, e a regra mora no banco (`sec_acesso_cancelado`) — aqui não há
 * cópia dela, como não há cópia de regra de acesso em nenhuma tela do hub.
 *
 * `entregue_em` vem NULO em 15.331 dos 15.337 entregues: a carga do Bubble não
 * trouxe a data da entrega. Por isso `criado_em` vem junto, e é a TELA que
 * escolhe a frase — chamar a data do pedido de data da entrega seria afirmar o
 * que não se sabe.
 */
export type DocumentoDoAcervo = {
  id: string;
  tipo_id: string | null;
  nome: string;
  emissao: string;
  curso_slug: string | null;
  curso_nome: string | null;
  anexo_path: string | null;
  anexo_externo_url: string | null;
  entregue_em: string | null;
  criado_em: string;
  /**
   * O ANO a que o documento se refere, quando ele se repete por período
   * (000282). Hoje só o informe de rendimentos; nulo nos demais.
   *
   * Sem ele a tela mostraria duas linhas "Informe de rendimentos" e o aluno
   * teria de adivinhar qual é a do IR que está declarando — e os dois NÃO são o
   * mesmo documento, então o selo de "2ª via" seria pior que nada aqui.
   */
  competencia_ano: number | null;
};

export async function lerMeuAcervo(): Promise<DocumentoDoAcervo[]> {
  const { data, error } = await supabase.rpc("sec_meu_acervo");
  if (error) throw error;
  return (data ?? []) as DocumentoDoAcervo[];
}

// ---------------------------------------------------------------------------
// A PUBLICAÇÃO pela secretaria (000282/000283)
// ---------------------------------------------------------------------------

/** Um curso em que o aluno ESTEVE — inclusive encerrado ou cancelado. */
export type CursoDoAluno = {
  curso_slug: string;
  curso_nome: string;
  origem: string;
  ultimo_fim: string | null;
};

export async function lerCursosDoAluno(userId: string): Promise<CursoDoAluno[]> {
  const { data, error } = await supabase.rpc("sec_cursos_do_aluno", { p_user: userId });
  if (error) throw error;
  return (data ?? []) as CursoDoAluno[];
}

/** Os tipos que a equipe PUBLICA (hoje só o informe de rendimentos). */
export async function lerTiposDePublicacao(): Promise<TipoDeDocumento[]> {
  const { data, error } = await supabase
    .from("sec_tipos")
    .select("id, nome, fluxo, prazo_dias, exige_pagamento, ordem, obrigatorio, exige_conclusao")
    .eq("ativo", true)
    .eq("fluxo", "publicacao")
    .order("nome");
  if (error) throw error;
  return (data ?? []) as TipoDeDocumento[];
}

/**
 * Publica um documento no acervo do aluno.
 *
 * São DUAS escritas, e a ordem é a mesma do envio do aluno: o ARQUIVO primeiro.
 * Se a linha entrasse antes e o upload falhasse, o acervo mostraria um documento
 * com um botão de baixar que estoura em 404 — e o aluno leria isso como
 * documento perdido.
 *
 * O caminho é montado com o id do ALUNO, e não com o de quem sobe. A policy do
 * bucket libera pela pasta (`foldername[1] = meu_profile_id()`), então arquivo
 * na pasta do operador é arquivo que o dono não baixa — o banco recusa isso de
 * qualquer forma (`sec_publicar_documento` confere), e aqui a montagem já nasce
 * certa.
 */
export async function publicarDocumento(args: {
  userId: string;
  tipoId: string;
  cursoSlug: string;
  ano: number | null;
  arquivo: File;
}): Promise<{ ok: boolean; erro?: string; detalhe?: string }> {
  const f = await farejarArquivo(args.arquivo);
  const mime = f.mime || args.arquivo.type || "application/pdf";
  const caminho = nomeNoBucket(args.userId, mime);

  const up = await supabase.storage.from("secretaria").upload(caminho, args.arquivo, {
    contentType: mime,
    upsert: false,
  });
  if (up.error) return { ok: false, erro: erroDeUpload(up.error.message ?? "") };

  const { data, error } = await supabase.rpc("sec_publicar_documento", {
    p_user: args.userId,
    p_tipo: args.tipoId,
    p_curso: args.cursoSlug,
    p_ano: args.ano,
    p_path: caminho,
  });
  if (error) {
    await supabase.storage.from("secretaria").remove([caminho]);
    return { ok: false, erro: error.message };
  }
  const d = (data ?? {}) as { ok?: boolean; detalhe?: string };
  if (!d.ok) {
    // A recusa veio do BANCO, então o arquivo órfão sai: sem isso, cada tentativa
    // recusada deixa um PDF no bucket que ninguém alcança e ninguém apaga.
    await supabase.storage.from("secretaria").remove([caminho]);
    return { ok: false, erro: d.detalhe ?? "não foi possível publicar" };
  }
  return { ok: true, detalhe: d.detalhe };
}

/**
 * O contrato assinado do aluno (000261).
 *
 * Ele NÃO é documento pedido ao balcão — estava em `sec_documentos` porque era
 * assim que o Bubble o mostrava, e saiu de lá por decisão de Victor em 16/09.
 * A tabela nova tem `revoke all`, então a leitura sai por função com filtro
 * explícito por aluno (a cicatriz da 000136).
 */
export type ContratoArquivo = {
  id: string;
  nome: string;
  curso_slug: string | null;
  anexo_path: string | null;
  anexo_externo_url: string | null;
  assinado_em: string | null;
  criado_em: string;
};

export async function lerMeusContratos(): Promise<ContratoArquivo[]> {
  const { data, error } = await supabase.rpc("contratos_meus_arquivos");
  if (error) throw error;
  return (data ?? []) as ContratoArquivo[];
}

/** O bucket é PRIVADO: a url é assinada e expira. */
export async function urlDoAnexo(path: string, segundos = 300): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("secretaria")
    .createSignedUrl(path, segundos);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export type ResultadoDoEnvio = { ok: true; id: string } | { ok: false; erro: string };

/**
 * Envia um documento: sobe o arquivo e cria a linha.
 *
 * São DUAS escritas, e a ordem importa. O arquivo primeiro: se a linha entrasse
 * antes e o upload falhasse, o aluno veria "em análise" sem arquivo — e a
 * secretaria abriria um documento que não existe.
 *
 * O caminho é `<auth.uid()>/<uuid>.<ext>`, como o avatar (000042): a pasta é a
 * identidade, então a policy do bucket não precisa consultar tabela nenhuma.
 */
/**
 * Quantos arquivos um envio aceita. Decisão de Victor em 02/09.
 *
 * O banco impõe o mesmo teto por GEOMETRIA (`sec_anexos.ordem between 1 and 5`
 * mais o unique por documento+ordem, 000162), então este número é só o que a
 * tela diz antes de o aluno tentar — não é a defesa.
 *
 * Nota do dado: 12 dos 3.850 slots com repetição têm 6 a 9 páginas (0,3%). Esses
 * alunos precisam juntar num PDF antes, que é o comportamento que a decisão quer
 * produzir.
 */
export const MAX_ARQUIVOS_POR_ENVIO = 5;

/**
 * Quantos bytes bastam para farejar o tipo.
 *
 * O maior teto que o farejador usa é 4096 (a distinção DOCX × ZIP lê o nome do
 * primeiro arquivo do pacote); a busca do `%PDF-` vai até 1024. 8 KiB dá folga
 * para as duas e evita ler 15 MB na memória do navegador só para olhar o
 * cabeçalho.
 *
 * O que este pedaço NÃO responde é `temSenha()`, que precisa do arquivo inteiro —
 * e ele não roda aqui: é EXPLICADOR pós-falha, e a falha acontece do outro lado.
 */
const BYTES_PARA_FAREJAR = 8192;

/** O tipo de um arquivo escolhido pelo aluno, pelos BYTES. */
async function farejarArquivo(arquivo: File) {
  const cabeca = await arquivo.slice(0, BYTES_PARA_FAREJAR).arrayBuffer();
  return farejar(new Uint8Array(cabeca));
}

/**
 * O nome do objeto no bucket e o `contentType` do upload, os dois pelo CONTEÚDO.
 *
 * O defeito que isto conserta é DURO, não silencioso: o bucket `secretaria` tem
 * `allowed_mime_types` com seis entradas, e `contentType: arquivo.type ||
 * undefined` manda `application/octet-stream` quando o navegador não sabe o tipo
 * — o que o bucket RECUSA. Arquivo sem extensão no nome (105 casos na origem, PDF
 * em 14 de 14 amostrados) simplesmente não subia.
 *
 * E a extensão do nome vinha de `arquivo.name.split(".").pop()`, que é a mesma
 * leitura de extensão que fez o `Switch` do n8n recusar 80 HEIC. Agora as duas
 * saem do farejador.
 */
function nomeNoBucket(uid: string, mime: string): string {
  return `${uid}/${crypto.randomUUID()}.${extensaoDoMime(mime)}`;
}

/**
 * A mensagem do Storage é técnica; o aluno recebe o que ele pode FAZER.
 *
 * O número aqui é o `file_size_limit` do bucket `secretaria`, e ele MORA em dois
 * lugares — nesta frase e na configuração do bucket. Já divergiram: a `000174`
 * subiu o bucket de 15 para 100 MB (o legado tem arquivo de 15,84 MB, e ele era
 * recusado) e esta frase continuou dizendo 15, ou seja acusava um limite que não
 * existia mais. Se o limite mudar de novo, muda AQUI também.
 */
function erroDeUpload(bruta: string): string {
  return /too large|exceeded/i.test(bruta)
    ? "Um dos arquivos passou de 100 MB. Tente uma foto menor ou um PDF."
    : /mime|content type|not supported/i.test(bruta)
      ? "Formato não aceito. Envie PDF, JPG, PNG, WEBP ou HEIC."
      : "Não conseguimos enviar o arquivo. Tente de novo.";
}

/**
 * Envia um documento com um OU VÁRIOS arquivos (até 5).
 *
 * O pedido de Victor em 02/09: *"um envio pode conter mais de um documento para o
 * gemini e a secretaria avaliar"*. O caso que motivou está medido — um aluno
 * mandou o histórico em 9 envios de 20 em 20 segundos, e como a tela mostra só o
 * último ele leu "Reprovado: não contém o nome" com sete páginas aprovadas
 * invisíveis.
 *
 * ---------------------------------------------------------------------------
 * A LIMPEZA é de TODOS os arquivos, não do último
 * ---------------------------------------------------------------------------
 * Se o terceiro upload falha, os dois primeiros já estão no bucket. E se a RPC
 * recusa, os cinco estão. Nos dois casos o `remove` leva a lista inteira: arquivo
 * órfão no bucket é documento pessoal de aluno guardado sem nada que o explique —
 * a mesma razão da versão de um arquivo, agora com cinco chances de acontecer.
 */
export async function enviarDocumento(
  tipoId: string,
  cursoSlug: string | null,
  arquivos: File | File[],
): Promise<ResultadoDoEnvio> {
  const lista = Array.isArray(arquivos) ? arquivos : [arquivos];
  if (lista.length === 0) return { ok: false, erro: "Escolha o arquivo para enviar." };
  if (lista.length > MAX_ARQUIVOS_POR_ENVIO) {
    return {
      ok: false,
      erro: `Envie no máximo ${MAX_ARQUIVOS_POR_ENVIO} arquivos por documento. Se tiver mais páginas, junte num PDF.`,
    };
  }

  const { data: sessao } = await supabase.auth.getUser();
  const uid = sessao?.user?.id;
  if (!uid) return { ok: false, erro: "Sessão expirada. Entre de novo." };

  const caminhos: string[] = [];
  const mimes: string[] = [];
  const bytes: number[] = [];

  // ── o formato é conferido ANTES de subir nada ──────────────────────────────
  //
  // A pergunta é sobre a ENTRADA, então é feita à entrada — a lição da 000075.
  // Subir cinco arquivos e descobrir na análise que o terceiro é um .docx
  // custaria o upload, a criação do documento e uma reprovação que o aluno lê
  // como julgamento do conteúdo.
  //
  // Quem NÃO é recusado aqui: o `desconhecido`. "Não sei" não é "não serve" (a
  // cicatriz do coalesce da 000135), e do outro lado o mime declarado pelo
  // navegador ainda tem uma chance.
  const farejados: string[] = [];
  for (const [i, arquivo] of lista.entries()) {
    const f = await farejarArquivo(arquivo);
    if (f.familia === "recusado" || f.familia === "converter") {
      const qual = lista.length > 1 ? `O arquivo ${i + 1} (${arquivo.name}): ` : "";
      return {
        ok: false,
        erro: `${qual}${f.motivoAoAluno ?? "este formato não é aceito. Envie PDF, JPG ou PNG."}`,
      };
    }
    // Bucket e prompt recebem o mime dos BYTES. Quando eles não dizem nada, o do
    // navegador serve de pista — e se ele também for vazio, o upload usaria
    // octet-stream e o bucket recusaria, então a última linha é o PDF: é o que 14
    // de 14 dos arquivos sem extensão amostrados de fato eram.
    farejados.push(f.mime ?? arquivo.type ?? "");
  }

  for (const [i, arquivo] of lista.entries()) {
    const mime = farejados[i] || "application/pdf";
    const caminho = nomeNoBucket(uid, mime);

    const up = await supabase.storage.from("secretaria").upload(caminho, arquivo, {
      contentType: mime,
      upsert: false,
    });
    if (up.error) {
      // Os que já subiram saem: ver o comentário acima.
      if (caminhos.length) await supabase.storage.from("secretaria").remove(caminhos);
      return { ok: false, erro: erroDeUpload(up.error.message ?? "") };
    }
    caminhos.push(caminho);
    mimes.push(mime);
    bytes.push(arquivo.size);
  }

  const { data, error } = await supabase.rpc("sec_enviar_documento_com_anexos", {
    p_tipo: tipoId,
    p_curso_slug: cursoSlug,
    p_caminhos: caminhos,
    p_mimes: mimes,
    p_bytes: bytes,
  });
  if (error) {
    await supabase.storage.from("secretaria").remove(caminhos);
    return { ok: false, erro: error.message };
  }
  return { ok: true, id: data as string };
}

/** Um arquivo de um envio, na ordem em que o aluno o escolheu (000162). */
export type AnexoDoEnvio = {
  ordem: number;
  anexo_path: string | null;
  anexo_externo_url: string | null;
  mime: string | null;
  bytes: number | null;
  /** `false` na linha que vem da coluna antiga — os 118.519 envios do legado. */
  da_tabela: boolean;
};

/**
 * Os arquivos de um envio. Serve ao aluno e à equipe — o banco confere o dono.
 *
 * Une os dois lugares onde o arquivo pode estar: `sec_anexos` (envios novos) e a
 * coluna de `sec_documentos` (os 118.519 antigos, que respondem como ordem 1).
 * A união mora no BANCO de propósito: se ela ficasse aqui, a fila da equipe
 * precisaria repeti-la, e seriam duas definições de "onde está o arquivo".
 */
export async function lerAnexos(documentoId: string): Promise<AnexoDoEnvio[]> {
  const { data, error } = await supabase.rpc("sec_anexos_do_documento", {
    p_documento: documentoId,
  });
  if (error) throw error;
  return (data ?? []) as AnexoDoEnvio[];
}

export async function solicitarDocumento(
  tipoId: string,
  cursoSlug: string | null,
): Promise<ResultadoDoEnvio> {
  const { data, error } = await supabase.rpc("sec_solicitar_documento", {
    p_tipo: tipoId,
    p_curso_slug: cursoSlug,
  });
  if (error) return { ok: false, erro: error.message };
  return { ok: true, id: data as string };
}

// ─────────────────────────────────────────────────────────────────────────────
// A fila da equipe
// ─────────────────────────────────────────────────────────────────────────────

export type ItemDaFila = {
  id: string;
  fluxo: "envio" | "solicitacao";
  status: StatusDeEnvio | StatusDeSolicitacao;
  nome: string;
  curso_slug: string | null;
  aluno_nome: string | null;
  aluno_email: string | null;
  anexo_path: string | null;
  anexo_externo_url: string | null;
  mensagem: string | null;
  criado_em: string;
  prazo_dias: number | null;
  dias_esperando: number;
  /**
   * O total da fila, repetido em cada linha.
   *
   * Não é redundância: sem ele a tela mostra 200 itens e o operador não sabe se a
   * fila tem 200 ou 12.625. "200 de 200" e "200 de 12.625" pedem trabalho
   * diferente.
   */
  total_na_fila: number;
  /**
   * O PDF que a secretaria vai conferir, e o número dele (000153).
   *
   * Vem de `sec_emitidos`, não do pedido: em `aguardando_conferencia` o
   * `anexo_path` do PEDIDO é nulo porque é ele que a policy do bucket usa para
   * liberar o download ao aluno. Sem estes dois campos a tela pediria para
   * conferir um documento que não tem como abrir.
   *
   * Nulo em toda linha que não é certificado esperando conferência.
   */
  emitido_path: string | null;
  emitido_numero: string | null;
  /**
   * Se ESTE pedido tem modelo automático (000157).
   *
   * A tela usa para decidir se oferece "Emitir agora". Sem isso o botão
   * apareceria nos 5.379 pedidos sem modelo (medido em 02/09), onde clicar
   * devolveria `sem_emissao_automatica` — e botão que sempre falha ensina o
   * operador a desconfiar da tela.
   *
   * Nunca nulo: quando o tipo saiu do catálogo, o banco responde `'nenhuma'`,
   * porque "não sei" aqui tem de significar "não emite".
   */
  tipo_emissao: "nenhuma" | TipoDeEmissao;
  /**
   * A turma do aluno NAQUELE curso (000159).
   *
   * Não está em `sec_documentos`: sai de `matriculas` → `pos_turmas`. Nula quando
   * o aluno não tem matrícula com turma no curso do documento — o que acontece
   * em curso livre e em matrícula sem turma definida.
   */
  turma_numero: number | null;
  /** O nome do tipo no catálogo de HOJE. `nome` pode ser o `nome_origem` histórico. */
  tipo_nome: string | null;
  /**
   * De qual instituição SAI O DIPLOMA daquela matrícula: `fbc` | `focus` (000252).
   *
   * O hub emite documento da FBC e nada mais (000248), então esta coluna é o que
   * permite à tela NÃO oferecer "Emitir agora" onde a emissão vai recusar. Sem
   * ela o operador via o botão aceso em 2.880 pedidos de aluno da Focus e
   * descobria o problema clicando.
   *
   * Nula quando a matrícula não registra a instituição — e nulo aqui não é
   * "fbc": a instituição vai IMPRESSA no documento, e a emissão recusa o nulo
   * com mensagem própria (peça o registro à secretaria antes de emitir).
   */
  instituicao: string | null;
};

/**
 * As opções dos filtros da fila, com a CONTAGEM de cada uma.
 *
 * A contagem não é enfeite: filtro que leva a zero resultado é pior que filtro
 * nenhum, porque o operador não sabe se filtrou errado ou se a fila esvaziou.
 */
export type FiltrosDaFila = {
  status: {
    valor: StatusDeEnvio | StatusDeSolicitacao;
    itens: number;
    /**
     * Este status entra na fila PADRÃO (000160)?
     *
     * `false` em `aprovado` e `reprovado`: eles são alcançáveis pelo filtro — a
     * equipe revisa as decisões da IA ali —, mas não esperam ação dela. Somá-los
     * à fila faria o cabeçalho dizer "30.273 esperando atendimento" quando quem
     * espera é o aluno.
     */
    esperando: boolean;
  }[];
  cursos: { slug: string; nome: string | null; itens: number }[];
  tipos: { nome: string; itens: number }[];
  /** As turmas que TÊM item na fila, amarradas ao curso — a turma 9 existe em vários. */
  turmas: { curso_slug: string; numero: number; itens: number }[];
  /**
   * A instituição de quem está na fila (000201). `sem` é o terceiro estado —
   * nenhuma matrícula do curso com instituição conhecida. Na tarde de
   * 09/09/2026 era o ÚNICO: `matriculas.instituicao` estava nula nas 43.386
   * matrículas (a coluna nasceu na 000137 e a carga veio só na 000203, à noite).
   * A tela lê esta lista e, quando não há `fbc` nem `focus`, DIZ isso em vez de
   * oferecer um combo com uma opção — foi assim que o filtro ligou sozinho
   * quando a carga entrou.
   */
  instituicoes: { valor: Instituicao; itens: number }[];
  /**
   * As gavetas de prazo, com a MESMA expressão que a linha da fila usa para
   * escrever "ATRASADO" (dias_esperando > prazo_dias). `vencendo` está DENTRO
   * de `no_prazo` (faltam até 5 dias); as outras três somam a fila.
   */
  prazos: Record<Prazo, number>;
  /** As pontas da fila, para os campos de data não convidarem a filtrar o nada. */
  periodo: { de: string | null; ate: string | null };
};

export type Instituicao = "fbc" | "focus" | "sem";
export type Prazo = "no_prazo" | "vencendo" | "atrasado" | "sem_prazo";
export const PRAZOS: readonly Prazo[] = ["no_prazo", "vencendo", "atrasado", "sem_prazo"];
export const INSTITUICOES: readonly Instituicao[] = ["fbc", "focus", "sem"];

/** Os filtros da fila além do fluxo e da busca — todos viajam na query da tela. */
export type FiltrosEscolhidos = {
  status?: string | null;
  curso?: string | null;
  turma?: string | null;
  tipo?: string | null;
  instituicao?: Instituicao | null;
  /** `aaaa-mm-dd`, como o `<input type="date">` entrega — o banco compara em `date`. */
  de?: string | null;
  ate?: string | null;
  prazo?: Prazo | null;
};

export async function lerFiltrosDaFila(
  fluxo: "envio" | "solicitacao" | null,
): Promise<FiltrosDaFila> {
  const { data, error } = await supabase.rpc("sec_fila_filtros", { p_fluxo: fluxo });
  if (error) throw error;
  const d = (data ?? {}) as Partial<FiltrosDaFila>;
  return {
    status: d.status ?? [],
    cursos: d.cursos ?? [],
    tipos: d.tipos ?? [],
    turmas: d.turmas ?? [],
    instituicoes: d.instituicoes ?? [],
    prazos: {
      no_prazo: d.prazos?.no_prazo ?? 0,
      vencendo: d.prazos?.vencendo ?? 0,
      atrasado: d.prazos?.atrasado ?? 0,
      sem_prazo: d.prazos?.sem_prazo ?? 0,
    },
    periodo: { de: d.periodo?.de ?? null, ate: d.periodo?.ate ?? null },
  };
}

export async function lerFila(
  fluxo: "envio" | "solicitacao" | null,
  busca: string | null,
  limite = 200,
  filtros: FiltrosEscolhidos = {},
): Promise<ItemDaFila[]> {
  const { data, error } = await supabase.rpc("sec_fila_da_equipe", {
    p_fluxo: fluxo,
    p_limite: limite,
    p_busca: busca,
    p_status: filtros.status ?? null,
    p_curso: filtros.curso ?? null,
    p_turma: filtros.turma ?? null,
    p_tipo: filtros.tipo ?? null,
    p_instituicao: filtros.instituicao ?? null,
    p_de: filtros.de ?? null,
    p_ate: filtros.ate ?? null,
    p_prazo: filtros.prazo ?? null,
  });
  if (error) throw error;
  return (data ?? []) as ItemDaFila[];
}

export async function avaliarEnvio(
  documentoId: string,
  aprovado: boolean,
  mensagem: string | null,
): Promise<{ ok: boolean; erro?: string }> {
  const { error } = await supabase.rpc("sec_avaliar_envio", {
    p_documento: documentoId,
    p_aprovado: aprovado,
    p_mensagem: mensagem,
  });
  if (error) return { ok: false, erro: error.message };
  return { ok: true };
}

/**
 * Devolve um envio à fila de análise, para o botão de REPROCESSAR do balcão
 * (000211).
 *
 * Duas etapas na tela, e a ordem é obrigatória: `sec_dados_para_analise` recusa
 * quem não está `em_analise` (motivo `ja_decidido`, *"a análise não desfaz
 * decisão"*), então o envio reprovado precisa voltar à fila ANTES de a Edge
 * Function ser chamada. Quando o envio já está na fila — o caso comum, a lista
 * padrão do balcão — a RPC responde `ok` sem escrever nada.
 *
 * Quem decide se pode é o BANCO: aprovado não volta (o aluno perderia um
 * documento certo) e reprovação de uma PESSOA não é desfeita pela IA. O
 * `detalhe` é a frase que a tela mostra — ela diz o caminho, e é por isso que
 * não se monta mensagem aqui.
 */
export type ResultadoDaReabertura = {
  ok: boolean;
  motivo: string;
  detalhe: string | null;
};

export async function reabrirParaAnalise(documentoId: string): Promise<ResultadoDaReabertura> {
  const { data, error } = await supabase.rpc("sec_reabrir_para_analise_pelo_painel", {
    p_documento: documentoId,
  });
  if (error) return { ok: false, motivo: "falha", detalhe: error.message };
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    ok: d.ok === true,
    motivo: String(d.motivo ?? "falha"),
    detalhe: typeof d.detalhe === "string" ? d.detalhe : null,
  };
}

export async function entregarSolicitacao(
  documentoId: string,
  arquivo: File,
): Promise<{ ok: boolean; erro?: string }> {
  const { data: sessao } = await supabase.auth.getUser();
  if (!sessao?.user?.id) return { ok: false, erro: "Sessão expirada." };

  // A pasta é do DONO do pedido, nunca de quem entrega (000284).
  //
  // Até 22/09 o caminho saía do `auth.uid()` do OPERADOR, e a policy do bucket
  // libera o download pela pasta — então o aluno recebia "documento pronto" e
  // não conseguia abrir. O banco agora recusa qualquer outra pasta; esta
  // leitura é o que faz a tela montar o caminho certo em vez de só ser recusada.
  //
  // `select` direto é o caminho previsto aqui: a policy de `sec_documentos` tem
  // o ramo `eh_suporte()` justamente para a equipe ler o pedido que atende.
  const { data: pedido, error: erroPedido } = await supabase
    .from("sec_documentos")
    .select("user_id")
    .eq("id", documentoId)
    .maybeSingle();
  if (erroPedido || !pedido?.user_id) {
    return { ok: false, erro: "Não encontrei este pedido. Recarregue a fila e tente de novo." };
  }

  // Pelos BYTES, como no envio do aluno. Aqui quem sobe é a EQUIPE, entregando o
  // documento pronto — então o formato não é recusado: se o operador está
  // entregando um .zip, ele sabe o que está fazendo, e o bucket é a defesa. O que
  // se conserta é o `contentType` errado, que fazia o upload falhar.
  const f = await farejarArquivo(arquivo);
  const mime = f.mime || arquivo.type || "application/pdf";
  const caminho = nomeNoBucket(pedido.user_id as string, mime);
  const up = await supabase.storage.from("secretaria").upload(caminho, arquivo, {
    contentType: mime,
    upsert: false,
  });
  if (up.error) return { ok: false, erro: up.error.message ?? "falha ao subir o arquivo" };

  const { error } = await supabase.rpc("sec_entregar_solicitacao", {
    p_documento: documentoId,
    p_anexo_path: caminho,
  });
  if (error) {
    await supabase.storage.from("secretaria").remove([caminho]);
    return { ok: false, erro: error.message };
  }
  return { ok: true };
}

export async function confirmarPagamento(
  documentoId: string,
): Promise<{ ok: boolean; erro?: string }> {
  const { error } = await supabase.rpc("sec_confirmar_pagamento", { p_documento: documentoId });
  if (error) return { ok: false, erro: error.message };
  return { ok: true };
}

/**
 * A validação manual do certificado emitido (000152).
 *
 * Aprovar entrega o PDF ao aluno e enfileira o aviso por e-mail; recusar devolve
 * o pedido à fila com o motivo, e a linha recusada fica em `sec_emitidos` como
 * histórico de tentativas.
 *
 * A RPC devolve `{ok:false, motivo, detalhe}` em vez de estourar quando o pedido
 * não está em conferência ou a recusa vem sem motivo — cada um pede uma ação
 * diferente do operador.
 */
export async function conferirCertificado(
  documentoId: string,
  aprovado: boolean,
  motivo: string | null,
): Promise<{ ok: boolean; erro?: string }> {
  const { data, error } = await supabase.rpc("sec_conferir_certificado", {
    p_documento: documentoId,
    p_aprovado: aprovado,
    p_motivo: motivo,
  });
  if (error) return { ok: false, erro: error.message };
  const r = (data ?? {}) as { ok?: boolean; detalhe?: string; motivo?: string };
  if (r.ok !== true) return { ok: false, erro: r.detalhe ?? r.motivo ?? "não deu certo" };
  return { ok: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// O PAGAMENTO da taxa e o comprovante (000242)
// ─────────────────────────────────────────────────────────────────────────────

/** Um comprovante que o aluno já mandou, e o que a secretaria disse dele. */
export type ComprovanteEnviado = {
  seq: number;
  enviado_em: string;
  situacao: "em_analise" | "aprovado" | "recusado";
  motivo: string | null;
  decidido_em: string | null;
};

/**
 * O que o popup de pagamento precisa saber, numa ida só.
 *
 * Tudo vem do banco (`sec_meu_pagamento`): se cobra, qual o link, qual foi a
 * última recusa e o histórico. A tela não decide se cobra — a regra é do par
 * (tipo, instituição) e mora em `sec_taxas`, e uma segunda conta aqui divergiria
 * no dia em que a FBC passasse a cobrar também.
 */
export type MeuPagamento = {
  estado: string;
  cobra: boolean;
  link: string | null;
  /** A recusa mais recente, servida só enquanto o pedido espera pagamento. */
  recusa: string | null;
  comprovantes: ComprovanteEnviado[];
};

export async function lerMeuPagamento(documentoId: string): Promise<MeuPagamento | null> {
  const { data, error } = await supabase.rpc("sec_meu_pagamento", { p_documento: documentoId });
  if (error) return null;
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) return null;
  return {
    estado: String(r.estado ?? ""),
    cobra: r.cobra === true,
    link: (r.link as string | null) ?? null,
    recusa: (r.recusa as string | null) ?? null,
    comprovantes: (r.comprovantes ?? []) as ComprovanteEnviado[],
  };
}

/**
 * Sobe o comprovante e o liga ao pedido.
 *
 * O mesmo desenho de `enviarDocumento`: o formato é conferido pelos BYTES ANTES
 * de subir (a pergunta é sobre a entrada, então é feita à entrada — a lição da
 * 000075), o arquivo vai para a pasta do próprio aluno (é o que a policy
 * `secretaria_envia` permite) e, se a RPC recusar, o arquivo SAI do bucket —
 * senão sobra lixo que ninguém referencia.
 */
export async function enviarComprovante(
  documentoId: string,
  arquivo: File,
): Promise<{ ok: true } | { ok: false; erro: string }> {
  const { data: sessao } = await supabase.auth.getUser();
  const uid = sessao?.user?.id;
  if (!uid) return { ok: false, erro: "Sessão expirada. Entre de novo." };

  const f = await farejarArquivo(arquivo);
  if (f.familia === "recusado" || f.familia === "converter") {
    return {
      ok: false,
      erro: f.motivoAoAluno ?? "este formato não é aceito. Envie PDF, JPG ou PNG.",
    };
  }
  const mime = f.mime ?? arquivo.type ?? "application/pdf";
  const caminho = nomeNoBucket(uid, mime);

  const up = await supabase.storage.from("secretaria").upload(caminho, arquivo, {
    contentType: mime,
    upsert: false,
  });
  if (up.error) return { ok: false, erro: erroDeUpload(up.error.message ?? "") };

  const { data, error } = await supabase.rpc("sec_enviar_comprovante", {
    p_documento: documentoId,
    p_anexo_path: caminho,
    p_mime: mime,
    p_bytes: arquivo.size,
  });
  if (error) {
    await supabase.storage.from("secretaria").remove([caminho]);
    return { ok: false, erro: error.message };
  }
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) {
    await supabase.storage.from("secretaria").remove([caminho]);
    return { ok: false, erro: String(r.detalhe ?? r.motivo ?? "não deu certo") };
  }
  return { ok: true };
}

// ── O lado da SECRETARIA ────────────────────────────────────────────────────

/** O comprovante como a equipe o vê: com o caminho, para abrir o arquivo. */
export type ComprovanteNoBalcao = ComprovanteEnviado & {
  anexo_path: string;
  mime: string | null;
  bytes: number | null;
  decidido_por: string | null;
};

export async function lerComprovantesDoPedido(
  documentoId: string,
): Promise<ComprovanteNoBalcao[]> {
  const { data, error } = await supabase.rpc("sec_comprovantes_do_pedido", {
    p_documento: documentoId,
  });
  if (error) return [];
  return (data ?? []) as ComprovanteNoBalcao[];
}

export async function aprovarComprovante(
  documentoId: string,
): Promise<{ ok: true; detalhe: string } | { ok: false; erro: string }> {
  const { data, error } = await supabase.rpc("sec_aprovar_comprovante", {
    p_documento: documentoId,
  });
  if (error) return { ok: false, erro: error.message };
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) return { ok: false, erro: String(r.detalhe ?? r.motivo ?? "não deu certo") };
  return { ok: true, detalhe: String(r.detalhe ?? "") };
}

export async function recusarComprovante(
  documentoId: string,
  motivo: string,
): Promise<{ ok: true; detalhe: string } | { ok: false; erro: string }> {
  const { data, error } = await supabase.rpc("sec_recusar_comprovante", {
    p_documento: documentoId,
    p_motivo: motivo,
  });
  if (error) return { ok: false, erro: error.message };
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) return { ok: false, erro: String(r.detalhe ?? r.motivo ?? "não deu certo") };
  return { ok: true, detalhe: String(r.detalhe ?? "") };
}

/**
 * Este pedido já está em andamento?
 *
 * Existe como função por uma razão medida: `aguardando_conferencia` nasceu na
 * 000149 e quebrou **quatro** lugares que enumeravam os status de solicitação,
 * cada um falhando de um jeito silencioso —
 *
 *   1. `sec_fila_da_equipe`      → o certificado emitido saía da fila e ninguém
 *                                  conferia (000153)
 *   2. `sec_solicitar_documento` → pedido duplicado passou a ser possível (000154)
 *   3. `sec_meus_emitidos`       → o aluno via o número antes da liberação (000154)
 *   4. `emAndamento`, nesta tela → o botão "Pedir de novo" aparecia durante a
 *                                  conferência, e o clique falhava na RPC
 *
 * Nenhum estourava; todos respondiam com confiança a coisa errada. Os três
 * primeiros são do banco e ficam lá; o quarto é do front, e a resposta passa a
 * ter um dono só. O dia em que um quinto status aparecer, muda AQUI.
 *
 * **E esse dia chegou:** `comprovante_em_analise` (000242). A instrução acima
 * foi seguida à risca, e os outros três lugares também — `sec_solicitar_documento`
 * ganhou o status na trava de pedido duplicado (sem isso o aluno que acabou de
 * anexar o comprovante abriria um SEGUNDO pedido e pagaria duas vezes), e
 * `sec_fila_da_equipe` o serve como gaveta própria. `sec_meus_emitidos` não
 * precisou: ele decide por EMISSÃO, não pelo estado do pedido, desde a 000155.
 */
export function pedidoEmAndamento(status: string | null | undefined): boolean {
  return (
    status === "aguardando_documento" ||
    status === "aguardando_pagamento" ||
    status === "comprovante_em_analise" ||
    status === "aguardando_conferencia"
  );
}

/**
 * Este pedido está no fluxo de PAGAMENTO — ou seja, o popup da taxa serve?
 *
 * Mora aqui pelo mesmo motivo de `pedidoEmAndamento`: são DOIS status, e a
 * pergunta é feita em dois lugares (o card do aluno e o balcão). Uma lista de
 * status escrita duas vezes é a mesma regra escrita duas vezes, e foi assim que
 * `aguardando_conferencia` quebrou quatro lugares em silêncio.
 */
export function pedidoDePagamento(status: string | null | undefined): boolean {
  return status === "aguardando_pagamento" || status === "comprovante_em_analise";
}

// ─────────────────────────────────────────────────────────────────────────────
// A emissão automática
// ─────────────────────────────────────────────────────────────────────────────

/**
 * O que a tela precisa saber depois de mandar emitir.
 *
 * `emitido: false` NÃO é erro de sistema — é o banco dizendo que este documento
 * não pode sair, com o motivo nomeado (falta dado cadastral, a trava barra, o
 * tipo não tem modelo automático) ou o emissor tendo falhado. Nos dois casos o
 * pedido continua vivo na fila da equipe, que é o caminho manual que já existia
 * antes desta frente — então nada se perde, e a mensagem diz o que aconteceu.
 */
export type ResultadoDaEmissao =
  | { emitido: true; tipo: TipoDeEmissao | null; numero: string; emConferencia: boolean }
  | {
      emitido: false;
      motivo: string;
      detalhe: string | null;
      /**
       * O `detalhe` serve para o ALUNO ler?
       *
       * Nem toda recusa é dele. "Complete no seu perfil: CPF, RG" é acionável;
       * "o endereço do emissor não está configurado — defina N8N_BASE_URL nos
       * secrets" é uma mensagem de infraestrutura, e mostrá-la a quem não pode
       * fazer nada com ela é a mesma falta da rota pública de orçamento, que
       * não pode ter "uma única mensagem de erro técnica" porque quem a abre não
       * tem suporte para acionar.
       *
       * A lista mora aqui, num lugar só, e não na tela: o dia em que um motivo
       * novo nascer, ele é classificado uma vez.
       */
      paraOAluno: boolean;
    };

/**
 * Quais recusas o aluno consegue resolver — ou pelo menos entender.
 *
 * Os motivos das cinco travas vêm de `sec_pode_solicitar` e são escritos PARA
 * ele ("Envie os 4 documentos obrigatórios — faltam 2"). `sem_emissao_automatica`
 * também entra: ele diz que a secretaria prepara à mão, que é informação útil e
 * verdadeira.
 *
 * Fora da lista ficam as falhas de sistema e as de configuração do curso — nelas
 * a tela diz que o pedido entrou e que a secretaria vai preparar, que é o que de
 * fato acontece, porque a fila manual continua existindo.
 */
const RECUSAS_DO_ALUNO = new Set([
  // As cinco travas (`sec_pode_solicitar_de`)
  "dados_incompletos",
  "documentos_faltando",
  "documentos_reprovados",
  "documentos_em_analise",
  "avaliacoes_pendentes",
  "prazo_minimo",
  // Estes dois dizem que o problema NÃO é do aluno, e um deles termina
  // literalmente com "está tudo certo do seu lado". Fora da lista, ele recebia o
  // genérico e perdia a única frase que responde "a culpa é minha?".
  "prazo_nao_configurado",
  "sem_data_de_matricula",
  // A emissão (`sec_dados_para_emissao`)
  "sem_emissao_automatica",
  "grade_nao_publicada",
  "sem_matricula",
  // A trava de estado da 000156: "já foi entregue, para uma segunda via peça de
  // novo" e "já está em conferência" são as duas coisas que o aluno mais precisa
  // ouvir quando clica e nada acontece.
  "estado_nao_permite",
]);

/**
 * Manda emitir o documento de um pedido.
 *
 * Chamada logo depois de `solicitarDocumento`, porque é assim que o Bubble faz
 * hoje e é esse comportamento que está sendo portado: *"quando o aluno solicita
 * a declaração ou certificado, o sistema envia o pedido pro n8n que emite o doc"*.
 *
 * NUNCA LANÇA. Emissão que falha não pode derrubar o pedido que já foi criado —
 * o aluno pediu, o pedido existe, e a secretaria prepara à mão se o emissor
 * estiver fora. É a mesma escolha de `abrirCard`, e o oposto de deixar o aluno
 * com uma tela de erro sobre algo que já deu certo.
 */
export async function emitirDocumento(documentoId: string): Promise<ResultadoDaEmissao> {
  try {
    const { data: sessao } = await supabase.auth.getSession();
    const token = sessao.session?.access_token;
    if (!token) {
      return {
        emitido: false,
        motivo: "sem_sessao",
        detalhe: "Entre de novo para continuar.",
        paraOAluno: true,
      };
    }

    const { data, error } = await supabase.functions.invoke("emitir-documento", {
      body: { documento_id: documentoId },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (error) {
      // O motivo útil está no CORPO, não em `error.message`: em 4xx/5xx o
      // supabase-js devolve `data: null` e guarda a resposta em `error.context`.
      // Sem ler daí, "o emissor não está configurado" e "este pedido não é seu"
      // viram a mesma tela genérica — a lição de `abrirCard`.
      if (error instanceof FunctionsHttpError) {
        try {
          const corpo = await error.context.json();
          const motivo = String(corpo?.motivo ?? "falha");
          return {
            emitido: false,
            motivo,
            detalhe: typeof corpo?.erro === "string" ? corpo.erro : (corpo?.detalhe ?? null),
            paraOAluno: RECUSAS_DO_ALUNO.has(motivo),
          };
        } catch {
          return { emitido: false, motivo: "falha", detalhe: null, paraOAluno: false };
        }
      }
      return { emitido: false, motivo: "falha", detalhe: null, paraOAluno: false };
    }

    const d = (data ?? {}) as Record<string, unknown>;
    if (d.emitido === true) {
      return {
        emitido: true,
        // Sem ternário: um tipo que esta versão não conhece vira `null`, e quem
        // desenha a mensagem diz "documento" em vez de chamá-lo pelo nome de
        // outro. A versão anterior escolhia "declaracao" para tudo que não
        // fosse certificado, e teria rotulado o histórico parcial errado.
        tipo: tipoDeEmissao(d.tipo),
        numero: String(d.numero ?? ""),
        emConferencia: d.em_conferencia === true,
      };
    }
    const motivo = String(d.motivo ?? "falha");
    return {
      emitido: false,
      motivo,
      detalhe: typeof d.detalhe === "string" ? d.detalhe : null,
      paraOAluno: RECUSAS_DO_ALUNO.has(motivo),
    };
  } catch {
    return { emitido: false, motivo: "falha", detalhe: null, paraOAluno: false };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Apresentação de estado — num lugar só
// ─────────────────────────────────────────────────────────────────────────────

/**
 * O rótulo, o tom e o que o aluno FAZ em cada status.
 *
 * Junto de propósito: espalhar isso pelas telas produziria dois textos para o
 * mesmo estado, e é justamente o estado que o aluno lê para decidir se precisa
 * agir. O `acao` é o que falta ELE fazer — nulo quando a bola está com a
 * secretaria.
 */
export function estadoDoDocumento(d: {
  fluxo: string;
  status: string;
  prazo_dias?: number | null;
}): { rotulo: string; tom: "ok" | "espera" | "erro"; acao: string | null } {
  switch (d.status) {
    case "aprovado":
      return { rotulo: "Aprovado", tom: "ok", acao: null };
    case "em_analise":
      return { rotulo: "Em análise", tom: "espera", acao: null };
    case "reprovado":
      return { rotulo: "Reprovado", tom: "erro", acao: "Corrija e envie de novo" };
    case "entregue":
      return { rotulo: "Documento entregue", tom: "ok", acao: null };
    case "aguardando_documento":
      return {
        rotulo: "Aguardando a secretaria",
        tom: "espera",
        acao: null,
      };
    case "aguardando_conferencia":
      // O certificado saiu do emissor e espera a conferência da secretaria. O
      // rótulo diz o que ESTÁ acontecendo, e não só que o aluno espera: "em
      // conferência" e "aguardando a secretaria" pedem paciência diferente, e
      // quem lê "aguardando" há três dias acha que o pedido não andou.
      return {
        rotulo: "Em conferência pela secretaria",
        tom: "espera",
        acao: null,
      };
    case "aguardando_pagamento":
      return {
        rotulo: "Aguardando pagamento",
        tom: "espera",
        // Este é o único status em que a bola está com o aluno e o hub não
        // resolve: a cobrança acontece fora, e eram 1.129 pedidos reais nesse
        // estado. Dizer "aguardando pagamento" sem dizer o que fazer deixaria o
        // aluno esperando por algo que não vai acontecer sozinho.
        //
        // 000242: a ação deixou de ser "fale com a secretaria" e passou a ser o
        // caminho de verdade — o aluno paga no link da instituição e anexa o
        // comprovante aqui. A frase antiga era pior do que vaga: para os 341
        // pedidos de aluno FBC medidos em 09/09 ela mandava pagar por algo
        // gratuito.
        acao: "Pague e anexe o comprovante",
      };
    case "comprovante_em_analise":
      // O rótulo diz o que ESTÁ acontecendo, e a bola voltou para a secretaria.
      // É a mesma distinção do `aguardando_conferencia`: "em análise" e
      // "aguardando" pedem paciência diferente.
      return {
        rotulo: "Comprovante em análise",
        tom: "espera",
        acao: null,
      };
    default:
      return { rotulo: d.status, tom: "espera", acao: null };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// A fila de AVISOS ao aluno
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Quantos avisos esperam envio.
 *
 * Existe para a fila ser VISÍVEL enquanto o provedor de e-mail não está
 * configurado. O hub não tem e-mail transacional hoje — os 13 templates de
 * `supabase/emails/` são do Auth, disparados pelo próprio Supabase —, então os
 * avisos acumulam. Fila invisível é como dado some sem ninguém ver, que é a
 * lição do relatório de carga onde nenhuma exclusão é silenciosa.
 */
export type AvisosPendentes = {
  tipo: string;
  pendentes: number;
  mais_antigo: string | null;
  com_erro: number;
};

export async function lerAvisosPendentes(): Promise<AvisosPendentes[]> {
  const { data, error } = await supabase.rpc("sec_avisos_pendentes");
  if (error) throw error;
  return (data ?? []) as AvisosPendentes[];
}

/**
 * O desfecho da drenagem. Nome próprio porque `ResultadoDoEnvio` já existe neste
 * arquivo, para o envio de DOCUMENTO pelo aluno — dois tipos com o mesmo nome
 * seriam a próxima confusão.
 */
export type ResultadoDaDrenagem =
  | { ok: true; enviados: number; falharam: number; pendentes: number }
  | { ok: false; erro: string; pendentes: number | null };

/**
 * Manda a Edge Function drenar a fila.
 *
 * Ela responde **503 com a contagem** quando não há provedor configurado, em vez
 * de dizer "enviei 0" — e é isso que a tela mostra ao operador. Dizer que enviou
 * quando não há como enviar é o silêncio que esta casa recusa.
 *
 * `functions.invoke` e não `fetch` cru: é a forma da casa (`abrir.ts`,
 * `admin.ts`), e ela cuida do token e da apikey. O detalhe do erro vem do CORPO
 * da resposta, não da mensagem do SDK — a function escreve texto para o operador
 * ler, e `error.message` diria só "non-2xx status code".
 */
export async function drenarAvisos(): Promise<ResultadoDaDrenagem> {
  const { data, error } = await supabase.functions.invoke("avisar-documento-pronto", {
    body: {},
  });
  const corpo = (data ?? {}) as Record<string, unknown>;
  if (error) {
    return {
      ok: false,
      erro: typeof corpo.erro === "string" ? corpo.erro : error.message,
      pendentes: typeof corpo.pendentes === "number" ? corpo.pendentes : null,
    };
  }
  return {
    ok: true,
    enviados: Number(corpo.enviados ?? 0),
    falharam: Number(corpo.falharam ?? 0),
    pendentes: Number(corpo.pendentes ?? 0),
  };
}

/**
 * O que a análise da IA fez com um envio.
 *
 * `aplicou` é a decisão do BANCO (`sec_registrar_analise`, 000164), e os três
 * valores dizem coisas diferentes ao aluno:
 *
 *   `aprovado`  — todos os arquivos foram julgados e passaram
 *   `reprovado` — algum tem falha DO ARQUIVO, e `mensagem` diz o que corrigir
 *   `nada`      — a falha foi NOSSA (indisponibilidade, limite, chave), e o
 *                 envio FICA em análise. É o desfecho que a origem não tinha:
 *                 86,3% das 7.036 reprovações de "não foi possível ler" vieram
 *                 de seis rajadas de indisponibilidade, e o aluno era acusado
 *                 por um arquivo que estava perfeito.
 */
export type ResultadoDaAnalise =
  | {
      analisado: true;
      aplicou: "aprovado" | "reprovado" | "nada";
      arquivos: number;
      julgados: number;
      reprovam: number;
      falhasNossas: number;
      mensagem: string | null;
    }
  | { analisado: false; motivo: string; detalhe: string | null };

/**
 * Dispara a análise dos arquivos de um envio.
 *
 * Chamada logo depois de `enviarDocumento`, que é o comportamento do Bubble
 * sendo portado: o aluno sobe o arquivo e a análise roda no mesmo clique. A
 * function autoriza pela POSSE (a linha é minha?) e escreve com a chave de
 * serviço.
 *
 * **Nunca lança.** Se ela falhar, o envio continua `em_analise` e a secretaria o
 * atende pela fila — que é o caminho que já existe. Deixar a exceção subir faria
 * a tela dizer "não conseguimos enviar" para um documento que ESTÁ no bucket e
 * na fila, e o aluno reenviaria: é a distinção entre erro de LEITURA e erro de
 * ESCRITA que o Calendário pagou (sem UMA escrita, há tela e falta um tique).
 */
export async function analisarDocumento(documentoId: string): Promise<ResultadoDaAnalise> {
  try {
    const { data: sessao } = await supabase.auth.getSession();
    const token = sessao.session?.access_token;
    if (!token) {
      return { analisado: false, motivo: "sem_sessao", detalhe: "Entre de novo para continuar." };
    }

    const { data, error } = await supabase.functions.invoke("analisar-documento", {
      body: { documento_id: documentoId },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (error) {
      // O motivo útil está no CORPO — em 4xx/5xx o supabase-js devolve
      // `data: null` e guarda a resposta em `error.context`. É a mesma leitura de
      // `emitirDocumento`, e sem ela "a chave do modelo não está configurada" e
      // "este envio não é seu" viram a mesma tela genérica.
      if (error instanceof FunctionsHttpError) {
        try {
          const corpo = await error.context.json();
          return {
            analisado: false,
            motivo: String(corpo?.motivo ?? "falha"),
            detalhe: typeof corpo?.erro === "string" ? corpo.erro : (corpo?.detalhe ?? null),
          };
        } catch {
          return { analisado: false, motivo: "falha", detalhe: null };
        }
      }
      return { analisado: false, motivo: "falha", detalhe: null };
    }

    const d = (data ?? {}) as Record<string, unknown>;
    if (d.analisado !== true) {
      return {
        analisado: false,
        motivo: String(d.motivo ?? "falha"),
        detalhe: typeof d.detalhe === "string" ? d.detalhe : null,
      };
    }
    return {
      analisado: true,
      aplicou: (d.aplicou === "aprovado" || d.aplicou === "reprovado" ? d.aplicou : "nada"),
      arquivos: Number(d.arquivos ?? 0),
      julgados: Number(d.julgados ?? 0),
      reprovam: Number(d.reprovam ?? 0),
      falhasNossas: Number(d.falhas_nossas ?? 0),
      mensagem: typeof d.mensagem === "string" ? d.mensagem : null,
    };
  } catch (e) {
    return {
      analisado: false,
      motivo: "falha",
      detalhe: e instanceof Error ? e.message : null,
    };
  }
}

/** A análise de cada arquivo de um envio, para o painel da secretaria (000164). */
export type AnaliseDoArquivo = {
  ordem: number;
  situacao: string;
  aprovado: boolean | null;
  motivos: string[] | null;
  status_geral: string | null;
  /**
   * `status_geral` discorda de `analise_tecnica.aprovado`?
   *
   * DERIVADO no banco, nunca guardado. O prompt tem dois campos de decisão, e
   * 904 linhas da origem os têm em desacordo — 55 reprovadas com o literal de
   * aprovação e 849 aprovadas com o de falha técnica. Antes disto o caso era
   * invisível: produzia um documento reprovado dizendo "aprovado com sucesso" e
   * ninguém sabia que havia acontecido 55 vezes.
   */
  divergente: boolean;
  modelo: string | null;
  mime_declarado: string | null;
  mime_farejado: string | null;
  erro: string | null;
  observacoes: string | null;
  /**
   * Quem reabriu o envio à mão, quando a linha é uma reabertura (000211).
   *
   * NULO quer dizer LOTE — as 9.215 linhas `reaberto` de hoje são do
   * reprocessamento de 02/09 e da reanálise de 09/09. "Foi o lote das 20h" e
   * "foi a Fulana às 14h32" são coisas diferentes para quem atende o aluno
   * amanhã, e a 000166 escreveu a razão de a reabertura ser registrada:
   * estado que muda sem rastro é estado mudando em silêncio.
   */
  reaberto_por_nome: string | null;
  criado_em: string;
};

/** As análises de um envio. Só a equipe — a RPC recusa quem não é suporte. */
export async function lerAnalises(documentoId: string): Promise<AnaliseDoArquivo[]> {
  const { data, error } = await supabase.rpc("sec_analises_do_documento", {
    p_documento: documentoId,
  });
  if (error) return [];
  return (data ?? []) as AnaliseDoArquivo[];
}
